import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  categorizeWorkspaces,
  getClientWorkspaces,
} from 'api/v3/access/clients';
import { UserCacheKeys } from 'types/cacheKeys.types';
import type { IWorkspace } from 'types/workspace.types';

const STALE_TIME_MS = 2 * 60 * 1000;
const WORKSPACES_PER_PAGE = 100;

export type UseClientWorkspacesOptions = {
  /** When false, the query does not run (stale cache may still be read if present). */
  enabled?: boolean;
};

export type CategorizedClientWorkspaces = {
  support: IWorkspace[];
  general: IWorkspace[];
};

export type UseClientWorkspacesReturn = {
  categorized: CategorizedClientWorkspaces;
  flatWorkspaces: IWorkspace[];
  /** True until all pages are loaded (including automatic fetchNextPage chain). */
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
};

/**
 * Client workspaces (accountants + support) for the logged-in customer, with InterVAT flags.
 *
 * - One React Query cache key for the whole app (`UserWorkspaces`); invalidation hits every screen.
 * - Fetches **all** API pages (auto `fetchNextPage` in an effect). Until then, `isLoading` stays true
 *   so callers don’t act on a partial list (e.g. eligibility with >100 links).
 * - `enabled: false` does not clear cache: you still read the last fetched data if another mount
 *   or an earlier visit populated this key (see `useIntervatAccountantPermission` + Data Access).
 */
export function useClientWorkspaces(
  options: UseClientWorkspacesOptions = {},
): UseClientWorkspacesReturn {
  const { enabled = true } = options;

  const query = useInfiniteQuery({
    queryKey: [UserCacheKeys.UserWorkspaces],
    queryFn: ({ pageParam }) =>
      getClientWorkspaces({
        page: pageParam,
        per_page: WORKSPACES_PER_PAGE,
        includeIntervatGrants: true,
      }),
    initialPageParam: 1,
    enabled,
    staleTime: STALE_TIME_MS,
    getNextPageParam: (lastPage) => {
      const paging = lastPage?.paging;
      if (paging && paging.page < paging.total_pages) {
        return paging.page + 1;
      }
      return undefined;
    },
  });

  const {
    data: workspacesPages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError: workspacesQueryError,
  } = query;

  useEffect(() => {
    if (
      !workspacesQueryError &&
      hasNextPage &&
      !isFetchingNextPage &&
      workspacesPages?.pages?.length
    ) {
      fetchNextPage();
    }
  }, [
    workspacesQueryError,
    hasNextPage,
    isFetchingNextPage,
    workspacesPages?.pages?.length,
    fetchNextPage,
  ]);

  const flatWorkspaces = useMemo(
    () => workspacesPages?.pages.flatMap((page) => page?.data ?? []) ?? [],
    [workspacesPages?.pages],
  );

  const categorized = useMemo(
    () => categorizeWorkspaces(flatWorkspaces),
    [flatWorkspaces],
  );

  const isLoading =
    enabled &&
    (query.isPending ||
      isFetchingNextPage ||
      (!!hasNextPage && !workspacesQueryError));

  return {
    categorized,
    flatWorkspaces,
    isLoading,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}
